import { Component, OnInit, OnDestroy, ViewChild, inject } from '@angular/core';;
import { ApplicationConfigService } from '../../services/application-config.service';
import { UserService } from '../../services/user/user.service';
import { BaseComponent } from "@nucleus/ui-base";
import { AuthenticationService } from '../../services/authentication.service';
import { ChangePasswordComponent } from '../../settings/change-password/change-password.component';
import { User } from '@nucleus/user-ui';
import { MatMenu } from '@angular/material/menu';
import { ThemeService } from '@nucleus/ui-common';
import { AboutUsComponent } from '../about-us/about-us.component';
import { PrivacyComponent } from '../privacy/privacy.component';
import { TermsComponent } from '../terms/terms.component';
import { SupportComponent } from '../support/support.component';
import { AppInterceptorService } from '../../services/app-interceptor.service';

@Component({
    selector: 'app-header',
    templateUrl: './header.component.html',
    styleUrl: './header.component.css',
    standalone: false
})
export class HeaderComponent extends BaseComponent implements OnInit, OnDestroy {

  @ViewChild('clientMenu', { static: true }) clientMenu!: MatMenu;
  @ViewChild('adminMenu', { static: true }) adminMenu!: MatMenu;
  @ViewChild('accountantMenu', { static: true }) accountantMenu!: MatMenu;
  @ViewChild('auditorMenu', { static: true }) auditorMenu!: MatMenu;
  @ViewChild('emptyMenu', { static: true }) emptyMenu!: MatMenu;
  
  themeService: ThemeService = inject(ThemeService);
  appConfigService : ApplicationConfigService = inject(ApplicationConfigService);
  authenticationService: AuthenticationService = inject(AuthenticationService);


  userService : UserService = inject(UserService);
  user: User = {} as User;

  constructor() {
    super();
  }

  ngOnInit(): void {
    this.userService.getMyProfile( {
      onStart: () => {},
      onSuccess: (user: User) => {
          this.user = user;
      },
      onError: (error) => {},
      onComplete: () => {}
    });
  }

  ngOnDestroy(): void {
  }

  // ── Help menu — replaces the standalone app-footer (NUC-79 real-estate) ──
  showAbout(): void {
    this.dialog.open(AboutUsComponent, {
      panelClass: 'bw-dialog-panel',
      width: '680px', maxWidth: '90vw', maxHeight: '85vh'
    });
  }

  showPrivacy(): void {
    this.dialog.open(PrivacyComponent, {
      panelClass: 'bw-dialog-panel',
      width: '680px', maxWidth: '90vw', maxHeight: '85vh'
    });
  }

  showTerms(): void {
    this.dialog.open(TermsComponent, {
      panelClass: 'bw-dialog-panel',
      width: '680px', maxWidth: '90vw', maxHeight: '85vh'
    });
  }

  showSupport(): void {
    this.dialog.open(SupportComponent, {
      panelClass: 'bw-dialog-panel',
      width: '520px', maxWidth: '90vw', autoFocus: false,
      data: {
        userEmail: this.appConfigService.getUserId(),
        tenant: this.appConfigService.getUserId(),
        sessionId: AppInterceptorService.getOrCreateSessionId(),
        appVersion: localStorage.getItem('version')
      }
    });
  }

  navigate(route: string) : void {
    this.router.navigate([route]);
  }

  /** True when the signed-in user can switch into the ops console — DevOps
   *  operators AND Administrators. Both enter the SAME console; because the
   *  console is capability-driven, an Administrator (whose console caps were
   *  trimmed to the admin + security surface — Users/Roles/Groups/IP-Blocklist/
   *  Audit/…) simply sees fewer menus than DevOps, who keep the full infra
   *  surface. App-only users (client/accountant) see nothing. */
  canSwitchToConsole(): boolean {
    // NUC-269: gate on the capability that governs ops-console entry (mirrors
    // nucleus-ui-console PermissionCheckerService.hasConsoleAccess() =
    // hasCapability(CONSOLE_ACCESS)). hasCapability() keeps ROLE_ADMINISTRATOR /
    // ROLE_DEVOPS as the universal escape hatch, so this is a strict superset of
    // the previous hasRoleDevOps()||hasRoleAdmin() check and additionally honours
    // any user explicitly granted console:access.
    return this.permissionChecker.hasCapability('console:access');
  }

  /** Top-level navigation to the ops console (<tenant> → console.<tenant>).
   *  Shared auth-server session means no re-login prompt. */
  switchToConsole(): void {
    const consoleUrl = this.appConfigService.getConsoleUrl()
      || `${window.location.protocol}//console.${window.location.host}/`;
    window.location.href = consoleUrl;
  }



  onLogout() : void {
    // Delegate to AuthenticationService.logout() which redirects to the
    // BFF /oauth2/reauthenticate endpoint (mirrors idle-timeout NUC-270 /
    // tasksense NUC-253). The previous flow called userService.logout()
    // which hits the *service backend* /oauth2/logout — that leaves the
    // BFF session cookie intact, so the subsequent /oauth2/authenticate
    // redirect found a still-valid session and silently re-authenticated.
    this.authenticationService.logout();
  }

  onChangePassword() : void {
    this.dialog.open(ChangePasswordComponent, {
      panelClass: 'bw-dialog-panel',
      width: '500px',
      data: { }
    });
  }

  onEditProfile() : void {
    this.router.navigate(['/settings/profile']);
  }

  async onSecurity(): Promise<void> {
    // Security is popup-only now — dynamic import keeps the security chunk lazy.
    const m = await import('../../settings/security/security.component');
    this.dialog.open(m.SecurityComponent, {
      panelClass: 'bw-dialog-panel',
      width: '560px',
      maxWidth: '95vw',
      maxHeight: '90vh',
      autoFocus: false,
      restoreFocus: false,
    });
  }

  /** Admin-only: the org-wide MFA administration screen (policy + who has it). */
  canManageMfa() : boolean {
    return (this.appConfigService.getAuthorities() || []).includes('mfa:access');
  }

  onMfaAdmin() : void {
    this.router.navigate(['/administration/mfa']);
  }

  // TODO NUC-269: persona display label + persona menu selection below are
  // inherently role-shaped (a "persona" IS a role) and are display/navigation
  // concerns, not access gates — there is no capability that means "show the
  // Administrator label" or "render the client menu vs the admin menu". Every
  // ACTION inside each persona menu is capability-gated (hasAccessToX). Left
  // role-based intentionally.
  getActiveRoleLabel(): string {
    if (this.permissionChecker.hasRoleAdmin()) return 'Administrator';
    if (this.permissionChecker.hasRoleAccountant()) return 'Accountant';
    if (this.permissionChecker.hasRoleAuditor()) return 'Auditor';
    if (this.permissionChecker.hasRoleClient()) return 'Client';
    return '';
  }

  getActiveMenu(): MatMenu {
    if (this.permissionChecker.hasRoleClient()) return this.clientMenu;
    if (this.permissionChecker.hasRoleAdmin()) return this.adminMenu;
    if (this.permissionChecker.hasRoleAccountant()) return this.accountantMenu;
    if (this.permissionChecker.hasRoleAuditor()) return this.auditorMenu;
    return this.emptyMenu;
  }

  // ── Collapsible client-nav sections (menu polish) ─────────────────────────
  // The sectioned client menu was too long with every group expanded at once.
  // Each section header now toggles its items; on open we restore the saved
  // collapse state (localStorage) and always reveal the section containing the
  // current route, so you land on your area without all four groups expanded.
  navExpanded: Record<string, boolean> = {};
  private readonly NAV_STATE_KEY = 'bw.clientNav.expanded';

  isNavSectionOpen(key: string): boolean { return !!this.navExpanded[key]; }

  toggleNavSection(key: string, ev: Event): void {
    ev.stopPropagation();
    this.navExpanded = { ...this.navExpanded, [key]: !this.navExpanded[key] };
    try { localStorage.setItem(this.NAV_STATE_KEY, JSON.stringify(this.navExpanded)); } catch { /* storage blocked */ }
  }

  /** Restore saved collapse state when the menu opens, always revealing the active section. */
  onNavMenuOpened(): void {
    try {
      const raw = localStorage.getItem(this.NAV_STATE_KEY);
      this.navExpanded = raw ? JSON.parse(raw) : {};
    } catch { this.navExpanded = {}; }
    this.navExpanded = { ...this.navExpanded, [this.activeNavSection()]: true };
  }

  /** The client-nav section that contains the current route (expanded by default). */
  private activeNavSection(): string {
    const u = this.router.url || '';
    if (/\/client\/(bank-statements|transactions|expenses|invoices|documents)/.test(u)) return 'bookkeeping';
    if (/\/client\/(financial-accounts|connected-apps|category-mapping|expense-mapping-rules|transaction-mapping-rules)/.test(u)) return 'accounting';
    if (/\/client\/(settings|fiscal-years)/.test(u)) return 'setup';
    return 'overview';
  }

}
